export interface CheckboxMenuOption<TValue extends string = string> {
    value: TValue;
    label: string;
    /** A short note after the label, such as "historical"; read with it. */
    description?: string;
}

export interface CheckboxMenuGroup {
    id: string;
    label: string;
}

/**
 * A run of options. With a `group`, the options sit under a header row whose
 * checkbox selects or clears exactly those options' values.
 */
export interface CheckboxMenuSection<TValue extends string = string> {
    group?: CheckboxMenuGroup;
    options: CheckboxMenuOption<TValue>[];
}

export type CheckboxMenuCheckState = boolean | 'indeterminate';

/** Whether a group checkbox follows a search (`visible`) or the whole section (`all`). */
export type CheckboxMenuGroupScope = 'visible' | 'all';

export interface CheckboxMenuProps<TValue extends string = string> {
    /** Translated text on the toggle button; also names the open menu. */
    label: string;
    /** Shown on the toggle after the label, such as what is selected now. */
    summary?: string;
    /**
     * Shows only the summary on the toggle, for a filter whose summary says what it is
     * ("All pens"); the label still names the toggle and the open menu.
     */
    hideLabel?: boolean;
    options: readonly CheckboxMenuSection<TValue>[];
    selected: readonly TValue[];
    onChange: (values: TValue[]) => void;
    /** Adds a field above the options that filters them by label, ignoring case. */
    searchable?: boolean;
    /**
     * What a group checkbox reflects and acts on while a search narrows its
     * section: the matches shown (`visible`, the default) or every option in it.
     */
    groupScope?: CheckboxMenuGroupScope;
    /** Aligns the open menu with the toggle's right edge. */
    alignRight?: boolean;
    /**
     * The id of a visible label beside the menu, as a form row has. The toggle then
     * shows only the summary, takes its name from that label and the summary, and fills
     * its slot like an input. `label` still names the open menu.
     */
    labelledBy?: string;
    disabled?: boolean;
    className?: string;
}

/**
 * Keeps the options whose label contains `query`, ignoring case, and drops
 * sections left without options so no header renders over nothing.
 */
export function filterSections<TValue extends string>(
    sections: readonly CheckboxMenuSection<TValue>[],
    query: string
): CheckboxMenuSection<TValue>[] {
    const needle = query.trim().toLowerCase();
    return sections
        .map((section) =>
            needle
                ? {
                      ...section,
                      options: section.options.filter((option) =>
                          option.label.toLowerCase().includes(needle)
                      ),
                  }
                : section
        )
        .filter((section) => section.options.length > 0);
}

export function sectionCheckState<TValue extends string>(
    options: readonly CheckboxMenuOption<TValue>[],
    selected: ReadonlySet<string>
): CheckboxMenuCheckState {
    const selectedCount = options.filter((option) => selected.has(option.value)).length;
    if (selectedCount === 0) return false;
    return selectedCount === options.length ? true : 'indeterminate';
}

/**
 * Adds or removes `values` and keeps every other selected value, including
 * ones no option lists (a value from a shared link, say), so toggling one
 * option never drops a selection the menu cannot show. Added values go last,
 * in the order given.
 */
export function applySelection<TValue extends string>(
    selected: readonly TValue[],
    values: readonly TValue[],
    checked: boolean
): TValue[] {
    if (checked) {
        const present = new Set<string>(selected);
        const added = values.filter(
            (value, index) => !present.has(value) && values.indexOf(value) === index
        );
        return [...selected, ...added];
    }
    const removed = new Set<string>(values);
    return selected.filter((value) => !removed.has(value));
}
